import { useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import { formatErrorForNotification } from '@/common/utils/error';
import {
  formatPrice,
  getBillingCycleLabel,
  getCouponValid,
  getNextBillDate,
  getPlanBreakdown,
  isLimitedPlan,
  PLAN_DETAILS,
} from '@/common/utils/license';
import { notifyZapier } from '@/common/utils/zapier';
import api from '@/db/apiClient';
import { Button } from '@shadcn/components/button';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function CheckoutReturn() {
  const { search } = useLocation();
  const navigate = useNavigate();

  const { organisation, isLoading, isFetching } = useOrganisation();

  const userId = useAppSelector(selectUserId);

  const timeout = useRef<NodeJS.Timeout | null>(null);
  const handled = useRef(false);

  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      if (!cancelled) {
        setShowFallback(true);
        notifyZapier({
          hook: 'error',
          userId: userId ?? 'Unknown',
          organisationId: organisation?.id ?? 'Unknown',
          notifier: 'CheckoutReturn',
          fatal: false,
          error: formatErrorForNotification(
            new Error(
              `‼️ URGENT - Checkout return timed out! Make sure this organisation (id: ${organisation?.id}) has had their subscription set up correctly.`,
            ),
          ),
        });
      }
    }, 10000);

    (async () => {
      try {
        if (isLoading || isFetching) return;

        if (
          !organisation ||
          !search ||
          !isLimitedPlan(organisation.license.plan)
        ) {
          throw new Error('Missing required information');
        }

        const license = organisation.license;
        const planDetails = PLAN_DETAILS[license.plan];
        const planName = planDetails.name;
        const seatCount = Math.max(license.seatCount, planDetails.seatCount);
        const billingInfo = license.billingInfo;
        const billingCycle = license.billingCycle;
        const currency = license.currency;
        const coupon = license.coupon;
        const price =
          billingCycle === 'monthly'
            ? planDetails.price / 10
            : planDetails.price;

        const urlParams = new URLSearchParams(search);

        const redirectStatus = urlParams.get('redirect_status');
        const paymentIntent = urlParams.get('payment_intent');

        if (
          redirectStatus === 'succeeded' &&
          paymentIntent &&
          !handled.current
        ) {
          handled.current = true;

          // Set session storage key
          sessionStorage.setItem('showPaymentSuccessDialogue', 'true');

          if (paymentIntent !== 'invoice') {
            // This is a card payment - notify Zapier
            const total = getPlanBreakdown(
              license.plan,
              seatCount,
              billingCycle,
              currency,
              coupon?.percentOff ?? null,
              getCouponValid(coupon?.appliedAt, billingCycle),
            ).total;
            const error =
              '‼️ Unknown - Check Stripe and ensure license has been set up correctly!';
            notifyZapier({
              hook: 'subscription',
              event: 'Card Payment Confirmed',
              userId: userId ?? error,
              organisationId: organisation.id,
              organisationName: organisation.name,
              name: billingInfo?.name || error,
              email: billingInfo?.email || error,
              plan: planName,
              seatCount,
              billingCycle,
              currency,
              coupon: coupon?.code ?? 'None',
              total: formatPrice(total, currency, true),
            });
          }

          await api.asEducator.sendSubscriptionConfirmedEmail({
            uid: userId!, // let the function fail if user is undefined so we get logs
            planName: planDetails.name,
            seats: seatCount,
            organisationName: organisation.name,
            amountDisplay: formatPrice(price, license.currency),
            intervalDisplay: getBillingCycleLabel(license.billingCycle),
            nextBillDateDisplay: dayjs(
              getNextBillDate(license.billingCycle, license.billingCycleAnchor),
            ).format('DD MMM YYYY'),
          });
        }

        if (!cancelled) {
          navigate('/dashboard/settings/billing', { replace: true });
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to confirm payment', {
          description:
            "Your payment may still have been processed. Please check your details. If it doesn't look right, please get in contact with support.",
        });
      } finally {
        if (timeout.current && !cancelled) clearTimeout(timeout.current);
      }
    })();

    return () => {
      cancelled = true;
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, organisation]);

  return (
    <div className="h-screen w-screen flex flex-col gap-4 items-center justify-center">
      <DataLoader />
      {showFallback && (
        <span className="text-sm">
          If you're not redirected in a few seconds,{' '}
          <Button
            variant="link"
            className="p-0"
            onClick={() => navigate('/dashboard/settings/billing')}
          >
            click here
          </Button>
        </span>
      )}
    </div>
  );
}
